import * as App from "@discovery/maestro";
import * as M from "@discovery/prelude/lib/data/maybe";
import { envBrowser } from "@discovery/maestro-env-browser";
import { pipe, constVoid } from "fp-ts/function";
import { log, logBus } from "@discovery/common-dplus/lib/bus";
import { startupException } from "@discovery/client-core";
import { componentMapper } from "./componentMapper";
import { Shimmer } from "@discovery/components-dplus/lib/components/atoms/shimmer";
import { getRealm } from "@discovery/common-dplus/lib/realm";
import { clientInformation } from "@discovery/common-dplus/lib/device-info";
import { compose } from "@discovery/prelude/lib/data/function";
import * as CQ from "@discovery/sonic-api-ng/lib/api/common/query";
import * as Routes from "@discovery/sonic-api-ng/lib/api/cms/routes";
import { mountCollection, mountRoutes, getRoute, } from "@discovery/template-engine/lib/mount";
import { getCollection } from "@discovery/sonic-api-ng/lib/api/cms/collections/endpoints";
import { routeLogger } from "./startup/logging";
import { userReq, patchUserReq, isProfileParentalMode } from "./startup/user";
import { userEntitlementsReq } from "./startup/user-entitlements";
import { tempEnvConfig } from "./temp";
import { errorPage } from "./startup/error-page";
import * as ErrorPages from "@discovery/components-dplus/lib/components/molecules/error-pages";
import { fromComponentMap } from "@discovery/template-engine/lib/template-engine";
import { cmsConfigReq } from "./startup/cmsConfig";
import { maestroHistory } from "@discovery/common-dplus/lib/hooks";
import { render } from "@discovery/maestro-render/lib/impl/react";
import * as Render from "@discovery/maestro-render";
import * as RTE from "fp-ts/ReaderTaskEither";
import * as TE from "fp-ts/TaskEither";
import * as O from "fp-ts/Option";
import * as E from "fp-ts/Either";
import * as R from "fp-ts/Reader";
import { sequenceS } from "fp-ts/Apply";
import * as History from "@discovery/maestro-history";
import * as S from "@discovery/prelude/lib/data/stream";
import { Spinner } from "@discovery/components-dplus/lib/components/atoms/spinner";
import { defaultDecorators } from "@discovery/common-dplus/lib/utils/config-types";
const onHistoryChange = (g, n = 1) => App.chainMerge((s) => App.execStream(S.map(g, S.take(n, s))), History.changes());
const queryParamGuard = (param) => (app) => onHistoryChange(({ searchParams }) => {
    return M.fromMaybe(searchParams.get(param), "") === "false"
        ? App.unit
        : app;
});
const runMainApp = ({ sonicApiAppConfig, clientEnvironment, authService, playerConfig, }) => {
    // TODO: investigate why typescript cant infer these types when inlining them
    const read = { sonicApiAppConfig, authService, playerConfig };
    const templateEngine = fromComponentMap(componentMapper, read, (t) => t);
    const defaultParams = Routes.Query.lenses.decorators.set(defaultDecorators);
    const selectHamburgerMenu = ({ clientEnvironment: { environment }, }) => {
        switch (environment) {
            case "test":
                return "hamburger-menu";
            case "uat":
                return "hamburger-menu-uat";
            default:
                return "hamburger-menu-v3";
        }
    };
    const hApp = mountCollection("header", templateEngine, getCollection(selectHamburgerMenu({ clientEnvironment })));
    const fApp = mountCollection("footer", templateEngine, getCollection("web-footer"));
    const bApp = mountRoutes("app", templateEngine, (h) => {
        if (h.location.pathname === "/home" || h.location.pathname === "/") {
            return App.chainMerge(routeLogger, getRoute("home", compose(defaultParams, Routes.Query.lenses.page.set(CQ.Pages.pages(CQ.Page.page(CQ.Page.ItemsIndex.NUMBER, 1), CQ.Page.page(CQ.Page.ItemsIndex.SIZE, 2))))(Routes.Query.parameters))(h));
        }
        else {
            return App.chainMerge(routeLogger, getRoute("home", compose(defaultParams)(Routes.Query.parameters))(h));
        }
    }, {
        onError: (e) => React.createElement(ErrorPages.ErrorPage, { errors: e, read: read }),
        onLoad: Shimmer,
    });
    const loaderApp = Render.mount(React.createElement(Spinner, { displayInCenterOfPage: true }), "app");
    const app = pipe(loaderApp, RTE.chainW(() => isProfileParentalMode === "true" ? patchUserReq : userReq), RTE.chainW(() => cmsConfigReq), RTE.chainW(() => userEntitlementsReq), RTE.chainW(() => sequenceS(RTE.ApplyPar)({
        h: queryParamGuard("displayMenu")(hApp),
        b: bApp,
        f: queryParamGuard("displayFooter")(fApp),
    })), RTE.apSecond(RTE.of(constVoid())));
    const appM = pipe(getElements, RTE.chainW((mountPoints) => pipe(app, R.local((r2) => (Object.assign(Object.assign({}, r2), render(mountPoints)))))));
    return appM;
};
const getElement = (id) => RTE.fromTaskEither(TE.fromIOEither(() => pipe(O.fromNullable(document.getElementById(id)), E.fromOption(() => startupException("Could not find element with id: " + id)))));
const getElements = sequenceS(RTE.ApplyPar)({
    app: getElement("app"),
    header: getElement("header"),
    footer: getElement("footer"),
    hamburger: getElement("hamburger"),
});
const getRealmServiceUrl = () => /disco-api.com|dev\d*.discoveryplus.in|concert.mercury.dnitv.com/.test(window.location.href)
    ? "https://dev-realmservice.mercury.dnitv.com"
    : "https://prod-realmservice.mercury.dnitv.com";
export const main = () => {
    const app = pipe(getRealm, RTE.chainW((realm) => {
        const sonicApiAppConfig = {
            domain: realm.domain,
            clientHostname: window.location.hostname,
            protocol: "https",
            clientName: "dplus-india",
            version: realm.environment,
            host: "sonic",
            realm: realm.realm,
            siteLookupKey: realm.siteLookupKey,
        };
        const args = {
            sonicApiAppConfig,
            playerConfig: {
                environment: realm.environment,
            },
            authService: {
                domain: realm.authDomain,
                protocol: "https",
            },
            // TODO: fix this envconfig. Should probably come from a capability
            clientEnvironment: Object.assign(Object.assign({}, tempEnvConfig), { environment: realm.environment }),
        };
        return pipe(runMainApp(args), RTE.local((r2) => (Object.assign(Object.assign({}, r2), args))));
    }));
    return App.run(app)(Object.assign(Object.assign(Object.assign({}, envBrowser), maestroHistory), { hostname: tempEnvConfig.hostname, realmServiceConfig: {
            realmServiceUrl: getRealmServiceUrl(),
        }, log: (l) => App.of(logBus.log(l)), clientInformation }))
        .then(E.fold(Promise.reject, Promise.resolve))
        .catch((error) => {
        const body = document.getElementById("app");
        if (body) {
            errorPage(body); //generic error message
        }
        window.newrelic.noticeError(error);
        let kind = "unknown";
        if (error.kind) {
            kind = error.kind;
        }
        else if (Array.isArray(error)) {
            for (const e of error) {
                if (e.kind) {
                    kind = e.kind;
                    break;
                }
            }
            const logMessage = {
                error: { message: "Concert error: " + kind },
            };
            log(logMessage);
            return console.error("Something went wrong", error);
        }
    });
};
